import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Swal from 'sweetalert2';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import marketing from '../assets/Video/ATO show reel 0.1.mp4';

const validationSchema = Yup.object().shape({
  name: Yup.string().min(2, 'Name must be at least 2 characters').required('Name is required'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
});

const HeroSection = ({ onFormFocus, onFormBlur }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      Swal.fire({
        title: 'Success!',
        text: 'We will contact you shortly',
        icon: 'success',
        confirmButtonColor: '#FD5F20',
        confirmButtonText: 'Great!',
        background: '#1a1a1a',
        color: '#fff',
      });

      reset();
    } catch (error) {
      Swal.fire({
        title: 'Oops...',
        text: 'Something went wrong!',
        icon: 'error',
        confirmButtonColor: '#FD5F20',
        background: '#1a1a1a',
        color: '#fff',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="relative z-10 h-full">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-screen">
          {/* Content Section */}
          <div className="flex flex-col justify-center space-y-8 py-8 lg:py-0">
            <div className="space-y-6">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#FD5F20] to-[#FF8C00] mb-2">
                  Grow Your Startup's
                </span>
                <span className="block text-gray-900 dark:text-white">
                  Online Success Today!
                </span>
              </h1>

              <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-xl">
                Ready to transform your online presence? We specialize in helping startups like yours achieve measurable growth through effective digital marketing strategies that deliver real results.
              </p>
            </div>

            {/* Video Section - Mobile */}
            <div className="lg:hidden my-4">
              <div className="relative w-full rounded-2xl overflow-hidden aspect-[16/9] shadow-2xl">
                <video
                  src={marketing}
                  title="Digital Marketing Services"
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
            </div>

            {/* Contact Form */}
            <div className="space-y-6">
              <div className="rounded-2xl p-6 shadow-xl border border-gray-100 dark:border-gray-700">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Start Your Journey
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                  Get your free consultation in 24 hours
                </p>
<form 
  onSubmit={handleSubmit(onSubmit)} 
  className="space-y-4  "
  onFocus={onFormFocus}
  onBlur={onFormBlur}

>
  <div className="grid grid-cols-1 gap-4 rounded-3xl  border border-[#FD5F20]/10 p-4">
    <div className="space-y-2">
      <input
        type="text"
        {...register('name')}
        className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all text-gray-900 dark:text-white dark:bg-gray-700 ${
          errors.name
            ? 'border-red-400 focus:ring-red-100 dark:focus:ring-red-900/20'
            : 'border-gray-200 dark:border-gray-600 focus:border-[#FD5F20] focus:ring-[#FD5F20]/20'
        }`}
        placeholder="Your Name"
      />
      {errors.name && (
        <p className="text-red-500 text-sm">
          {errors.name.message}
        </p>
      )}
    </div>

    <div className="space-y-2">
      <input
        type="email"
        {...register('email')}
        className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-4 transition-all text-gray-900 dark:text-white dark:bg-gray-700 ${
          errors.email
            ? 'border-red-400 focus:ring-red-100 dark:focus:ring-red-900/20'
            : 'border-gray-200 dark:border-gray-600 focus:border-[#FD5F20] focus:ring-[#FD5F20]/20'
        }`}
        placeholder="your.email@company.com"
      />
      {errors.email && (
        <p className="text-red-500 text-sm">
          {errors.email.message}
        </p>
      )}
    </div>
  </div>

  <motion.button
    type="submit"
    className="w-full px-6 py-4 bg-gradient-to-r from-[#FD5F20] to-[#FD5F20]/90 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed group"
    whileHover={{ scale: isValid ? 1.02 : 1 }}
    whileTap={{ scale: 0.98 }}
    disabled={isSubmitting || !isValid}
  >
    {isSubmitting ? (
      <span className="flex items-center justify-center">
        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Processing...
      </span>
    ) : (
      <span className="flex items-center justify-center group-hover:translate-x-1 transition-transform duration-200">
        Get Free Consultation
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2 transition-transform duration-200 group-hover:translate-x-1" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </span>
    )}
  </motion.button>

  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
    By submitting this form, you agree to our{' '}
    <a href="/Policy/PrivacyPolicy" className="text-[#FD5F20] hover:underline">privacy policy</a>
  </p>
</form>
              </div>
            </div>
          </div>

          {/* Video Section - Desktop */}
          <div className="hidden lg:flex items-center justify-center h-full">
            <div className="relative w-full h-full max-h-[80vh]">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl w-full h-full min-h-[500px]">
                <video
                  src={marketing}
                  title="Digital Marketing Services"
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent" />
                
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <div className="w-0 h-0 border-l-[12px] border-l-[#FD5F20] border-y-[8px] border-y-transparent ml-1"></div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Stats overlay */}
              <div className="absolute -bottom-6 -left-6 bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-xl">
                <div className="flex items-center space-x-4">
                  <div className="text-3xl font-bold text-[#FD5F20]">97%</div>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    Client satisfaction rate across all projects
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection;